import {
    ICampaignStatus,
    ICampaignType,
    ICampaignSummariesQuery,
    IProspectingCampaignStats,
    INurtureCampaignStats,
    ICampaignsWithOrgMemberQuery,
} from '@copilot/data/graphql/_generated';
import { CampaignSummaryModel } from './allCampaignsTable';
import { CampaignSummaryStats } from './allCampaignsPageTypes';
import { isNil, partial } from 'lodash';
import { CampaignFilterType } from './campaignTypeDropdown';
import { FilterDefinition } from '@copilot/common/components/componentModels/filterDefinition';

/**
 * Convert Campaigns with org member results to CampaignSummaryModel
 * @param summary
 * @param orgMemberIdToNameMap
 * @returns
 */
export function toCampaignSummaryModel(
    summary: ICampaignsWithOrgMemberQuery['campaignSummaries'][0]
): CampaignSummaryModel {
    const summaryType = summary.type ?? ICampaignType.Prospecting;
    return {
        id: summary.id,
        name: summary.name ?? '',
        type: summaryType,
        status: !isNil(summary.status) ? summary.status : ICampaignStatus.Enabled,
        stats: getStatsForSummaryModel(summary.stats, summaryType),
        numActiveMembers: !isNil(summary.numActiveMembers) ? summary.numActiveMembers : 0,
        members: summary.members,
        orgMember: {
            id: summary.orgMember.id,
            name: summary.orgMember.userInfo.fullName,
        },
        launchedAt: summary.launchedAt ?? undefined,
    };
}

/**
 * Convert legacy CampaignSummary results to CampaignSummaryModel
 * @param summary
 * @param orgMemberIdToNameMap A map of org member IDs to member names
 * @returns
 */
export function toLegacyCampaignSummaryModel(
    summary: ICampaignSummariesQuery['campaignSummaries'][0],
    orgMemberIdToNameMap: Record<string, string>
): CampaignSummaryModel {
    const summaryType = summary.type ?? ICampaignType.Prospecting;
    const orgMemberId = summary.members[0]?.orgMemberId;
    return {
        id: summary.id,
        name: summary.name ?? '',
        type: summaryType,
        status: !isNil(summary.status) ? summary.status : ICampaignStatus.Enabled,
        stats: getStatsForSummaryModel(summary.stats, summaryType),
        numActiveMembers: !isNil(summary.numActiveMembers) ? summary.numActiveMembers : 0,
        members: summary.members,
        orgMember: {
            id: orgMemberId ?? '',
            name: isNil(orgMemberId) ? '' : orgMemberIdToNameMap[orgMemberId],
        },
        launchedAt: summary.launchedAt ?? undefined,
    };
}

/**
 * Get stats for the Campaign Summary Model
 * @param stats The stats we're converting
 * @param campaignType The type of the campaign the stats are coming from
 * @returns The stats object in a CampaignSummaryModel
 */
function getStatsForSummaryModel(
    stats: CampaignSummaryStats,
    campaignType: ICampaignType
): CampaignSummaryModel['stats'] {
    let result: CampaignSummaryModel['stats'] = {
        numUserReplies: stats?.numUserReplies ?? 0,
        percentReplied: stats?.percentReplied ?? 0,
        numUsersConnected: undefined,
        numInvitesSent: undefined,
        percentConnected: undefined,
        numSendMessages: undefined,
    };
    if (isProspectingStats(stats, campaignType)) {
        result = {
            ...result,
            numUsersConnected: stats.numUsersConnected ?? 0,
            numInvitesSent: stats.numInvitesSent ?? 0,
            percentConnected: stats.percentConnected ?? 0,
        };
    } else if (isNurtureStats(stats, campaignType)) {
        result = {
            ...result,
            numSendMessages: stats.numSendMessages ?? 0,
        };
    }
    return result;
}

/**
 * Type check a prospecting stats object based on the originating campaign type
 * @param stats The stats object we're checking
 * @param campaignType The type of campaign it originated from
 */
function isProspectingStats(
    stats: CampaignSummaryStats,
    campaignType: ICampaignType
): stats is IProspectingCampaignStats {
    return campaignType === ICampaignType.Prospecting;
}

/**
 * Type check a nurture stats object based on the originating campaign type
 * @param stats The stats object we're checking
 * @param campaignType The type of campaign it originated from
 */
function isNurtureStats(
    stats: CampaignSummaryStats,
    campaignType: ICampaignType
): stats is INurtureCampaignStats {
    return campaignType === ICampaignType.Nurture;
}

/**
 * Helper function for filtering by campaign type
 * @param appliedFilter The filter applied
 * @param c The campaign to filter
 * @returns True if the campaign matches the filter
 */
function applyCampaignTypeFilter(appliedFilter: CampaignFilterType, c: CampaignSummaryModel) {
    return (
        appliedFilter === CampaignFilterType.All ||
        (c.type === ICampaignType.Prospecting &&
            appliedFilter === CampaignFilterType.Prospecting) ||
        (c.type === ICampaignType.Nurture && appliedFilter === CampaignFilterType.Nurture)
    );
}

/**
 * Helper function for filtering by team members
 * @param selectedMembers The team members to filter by
 * @param c The campaign to filter
 * @returns True if the campaign matches the filter
 */
function applyTeamMemberFilter(selectedMembers: FilterDefinition[], c: CampaignSummaryModel) {
    if (selectedMembers.length === 0) {
        return true;
    }
    return c.members.some((member) =>
        selectedMembers.some((filteringMember) => member.orgMemberId === filteringMember.key)
    );
}

/**
 * Helper function for filtering by campaign name
 * @param searchTerm The search term to filter by
 * @param c The campaign to filter
 * @returns True if the campaign matches the filter
 */
function applyCampaignNameFilter(searchTerm: string, c: CampaignSummaryModel) {
    return c.name.toLowerCase().includes(searchTerm.toLowerCase());
}

/**
 * Helper to apply filtering functions
 * @param campaigns The campaigns to filter
 * @param appliedFilter The filter applied
 * @param selectedMembers The team members to filter by
 * @param searchTerm The search term to filter by
 * @returns The filtered campaigns
 */
export function applyAllCampaignsFilters(
    campaigns: CampaignSummaryModel[],
    appliedFilter: CampaignFilterType,
    selectedMembers: FilterDefinition[],
    searchTerm: string
) {
    return campaigns
        .filter(partial(applyCampaignTypeFilter, appliedFilter))
        .filter(partial(applyTeamMemberFilter, selectedMembers))
        .filter(partial(applyCampaignNameFilter, searchTerm));
}
